import wordmark1x from '@core/assets/brand/wordmark.webp';
import wordmark2x from '@core/assets/brand/wordmark@2x.webp';

export interface WordmarkProps {
  /**
   * Maximum rendered width in CSS pixels. The mark shrinks below this on narrow
   * viewports rather than overflowing or being hidden — see the note about
   * 360px below.
   */
  width?: number;
  className?: string;
  /**
   * `true` when this is the only thing naming the product on the page, so it
   * needs an accessible name. `false` when adjacent text already says
   * "Printoman" — a duplicate would be read twice.
   */
  labelled?: boolean;
}

/**
 * The Printoman wordmark.
 *
 * A raster, not a vector, and deliberately so: the supplied "SVG" logos are
 * screenshot crops with a PNG embedded, carrying no vector geometry at all.
 * Re-encoded and trimmed, the wordmark is 12 KB at 1x — smaller than most
 * traced SVGs of the same artwork would be, and pixel-exact at the sizes it is
 * used. If a true vector original ever arrives, this component is the only
 * place that has to change.
 *
 * The transparent background is a flood-fill from the edges, so the white
 * letterforms survive: they are enclosed by the logo's own black outlines.
 *
 * It carries white letterforms, which is the lockup the guide calls "on
 * charcoal", and it is the only one of the three we have as artwork. That
 * sounds like it rules out a light page, and it does not: every letter is
 * drawn with a heavy black outline and sits on a black drop-shadow slab, so
 * the shape is defined by the black, not by the white. On white and on the
 * page's off-white it reads as a crisp outlined mark at every size we use.
 *
 * It was briefly wrapped in a charcoal plate here on the reasoning that white
 * on white cannot work. That reasoning ignored the outline, and the plate
 * read as a sticker pasted onto the header. Rendering it both ways at the
 * header's real 132px settled it.
 *
 * A true charcoal lockup would still be worth having for printed material,
 * and recolouring this raster does not produce one — dropping the white to
 * charcoal merges the letters into their own outlines. That needs the
 * designer's file, and this component is the only place that would change.
 */
export function Wordmark({ width = 168, className, labelled = true }: WordmarkProps) {
  // Native aspect ratio of the trimmed artwork, 752x248. Height is derived
  // rather than guessed so the image never reserves the wrong space and shifts
  // the layout as it decodes.
  const height = Math.round((width * 248) / 752);

  return (
    <img
      src={wordmark1x}
      srcSet={`${wordmark1x} 1x, ${wordmark2x} 2x`}
      width={width}
      height={height}
      alt={labelled ? 'Printoman — your 24/7 print hero' : ''}
      aria-hidden={labelled ? undefined : true}
      className={className}
      // The header mark is above the fold on every page; letting it lazy-load
      // would make the brand pop in after first paint.
      loading="eager"
      decoding="async"
      // FLUID, not fixed. A fixed inline width beats any responsive class, and
      // that is what forced the hero to hide the character badge below 375px —
      // which meant it never appeared on a 360px Android viewport, the single
      // most common width there is. `aspect-ratio` keeps the space reserved so
      // shrinking costs no layout shift.
      style={{ width: '100%', maxWidth: width, aspectRatio: '752 / 248', height: 'auto' }}
    />
  );
}
